import {
  createServiceDelivery,
  EMPTY_SERVICE_DELIVERY,
  placeOfServiceIdOf,
  treatmentServiceIdsOf,
} from 'src/contexts/treatmentPlanning';
import { computed, onMounted, ref } from 'vue';

import {
  createFixSessionDeliveries,
  createFixSessionPlanDelivery,
} from '../../application/useCases/fixSessionPlanDelivery';
import { parseSessionPlanDeclaration } from '../../domain/constants/holdTaskTypes';
import { fillsMissing, holdReasonFixedBy } from '../../domain/services/sessionPlanDeliveryFix';
import { heldSessionRepository, serviceDeliveryCatalogs } from '../../infrastructure/api/heldSessionApi';

const toDelivery = (serviceDelivery) => ({
  placeOfServiceId: placeOfServiceIdOf(serviceDelivery),
  treatmentServiceIds: treatmentServiceIdsOf(serviceDelivery),
});

const toServiceDelivery = (delivery) => (delivery ? createServiceDelivery(delivery) : EMPTY_SERVICE_DELIVERY);

/**
 * Everything fixing a missing service or place needs, for one of two scopes: the one session a biller
 * picked from billing items, or every session a plan ran, from its task.
 *
 * THE CHOICE STARTS FROM WHAT IS ALREADY THERE: a single session opens with the half it has, and a
 * plan's sessions with what the plan already declares.
 *
 * @param {{
 *   clinicId: string,
 *   section: string,
 *   sessionDelivery: import('../../application/ports/SessionDeliverySurfaces.port').SessionDeliverySurfaces,
 *   sessionPlanDeclaration?: string|null,
 *   sessionActivityId?: string|null,
 * }} props - exactly one of the last two
 */
// eslint-disable-next-line import/prefer-default-export
export function useSessionPlanDeliveryFix(props) {
  const isPlan = Boolean(props.sessionPlanDeclaration);
  const declaration = isPlan ? parseSessionPlanDeclaration(props.sessionPlanDeclaration) : null;
  const holdReason = holdReasonFixedBy(props.section);
  const fixSessionPlanDelivery = createFixSessionPlanDelivery({
    heldSessions: heldSessionRepository,
    sessionDelivery: props.sessionDelivery,
  });
  const fixSessionDeliveries = createFixSessionDeliveries({ sessionDelivery: props.sessionDelivery });
  const catalogs = serviceDeliveryCatalogs();
  const plan = ref(null);
  const serviceDates = ref([]);
  const sessionCount = ref(0);
  const canEditPlan = ref(false);
  const reading = ref(true);
  const failedToRead = ref(false);
  const chosen = ref(EMPTY_SERVICE_DELIVERY);

  const readPlanScope = async () => {
    const [loadedPlan, held] = await Promise.all([
      declaration ? props.sessionDelivery.readPlan(declaration.sessionPlanId, declaration.activityType) : null,
      heldSessionRepository.findHeldSessions(props.clinicId, props.sessionPlanDeclaration, holdReason),
    ]);

    plan.value = loadedPlan;
    sessionCount.value = new Set(held.map((item) => item.sessionActivityId)).size;
    serviceDates.value = [...new Set(held.map((item) => item.serviceDate).filter(Boolean))].sort();
    chosen.value = toServiceDelivery(loadedPlan?.declared);
    canEditPlan.value = loadedPlan?.clientId ? await props.sessionDelivery.canChangePlan(loadedPlan.clientId) : false;
  };

  const readSessionScope = async () => {
    const [session] = await props.sessionDelivery.readSessionDeliveries([props.sessionActivityId]);

    sessionCount.value = session ? 1 : 0;
    chosen.value = toServiceDelivery(session?.delivery);
  };

  onMounted(async () => {
    try {
      await (isPlan ? readPlanScope() : readSessionScope());
    } catch (error) {
      console.error('Failed to read the sessions to fix', error);
      failedToRead.value = true;
    } finally {
      reading.value = false;
    }
  });

  const canApply = computed(() => !reading.value
    && sessionCount.value > 0
    && fillsMissing(props.section, toDelivery(chosen.value)));

  /** @param {boolean} alsoOnPlan - ignored for a single session, which never changes its plan */
  const apply = async (alsoOnPlan) => {
    if (isPlan) {
      return fixSessionPlanDelivery({
        clinicId: props.clinicId,
        sessionPlanDeclaration: props.sessionPlanDeclaration,
        holdReason,
        chosen: toDelivery(chosen.value),
        alsoOnPlan,
      });
    }
    const outcome = await fixSessionDeliveries({
      sessionActivityIds: [props.sessionActivityId],
      chosen: toDelivery(chosen.value),
    });

    return { ...outcome, plan: 'untouched' };
  };

  return {
    ...catalogs,
    isPlan,
    activityType: declaration?.activityType ?? null,
    plan,
    serviceDates,
    sessionCount,
    canEditPlan,
    reading,
    failedToRead,
    chosen,
    canApply,
    apply,
  };
} // useSessionPlanDeliveryFix()
